const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
// const API_BASE_URL =  "http://localhost:8000/api";
console.log("🔧 API_BASE_URL:", API_BASE_URL);

/** 업로드/정적 파일 URL용 API 서버 오리진 (…/api 제거) */
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

/** 상대 경로 이미지 접두사 (레거시 /uploads 등) */
export const MEDIA_BASE_URL = API_ORIGIN.replace(/\/$/, "");

export function resolveMediaUrl(path) {
    if (!path) return "";
    /** Supabase Storage·외부 CDN 등 절대 URL 그대로 */
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    /** CRA public 정적 리소스 (/short_comics 등) — 같은 오리진 */
    if (normalized.startsWith("/short_comics") || normalized.startsWith("/public")) {
        return normalized;
    }
    /** 레거시 백엔드 로컬 업로드 (/uploads) */
    if (normalized.startsWith("/uploads")) {
        return `${MEDIA_BASE_URL}${normalized}`;
    }
    return `${MEDIA_BASE_URL}${normalized}`;
}

/** GET /api/stories 응답 정규화 */
export function normalizeStoriesResponse(data) {
    if (!data) return { stories: [], total: 0, page: 1, limit: 10 };
    if (Array.isArray(data)) return { stories: data, total: data.length, page: 1, limit: data.length };
    return {
        stories: data.stories || [],
        total: data.total ?? (data.stories?.length ?? 0),
        page: data.page ?? 1,
        limit: data.limit ?? (data.stories?.length ?? 0),
    };
}

class TraumaHelpAPI {
    async fetchJSON(url, options = {}) {
        try {
            console.log("🌐 Making request to:", url);
            console.log("📤 Request method:", options.method || 'GET');
            console.log("📤 Request headers:", options.headers);
            console.log("📤 Request body (raw):", options.body);
            
            // body를 파싱해서 실제 데이터 확인
            if (options.body) {
                try {
                    const parsedBody = JSON.parse(options.body);
                    console.log("📤 Request body (parsed):", parsedBody);
                } catch (e) {
                    console.log("📤 Body parsing failed:", e);
                }
            }

            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });

            console.log("📥 Response status:", response.status);
            console.log("📥 Response ok:", response.ok);
            console.log("📥 Response headers:", Object.fromEntries(response.headers.entries()));

            if (!response.ok) {
                const responseText = await response.text();
                console.error("❌ Response text:", responseText);
                
                let errorData;
                try {
                    errorData = JSON.parse(responseText);
                } catch (e) {
                    errorData = { detail: `HTTP error! status: ${response.status} - ${response.statusText}. Response: ${responseText}` };
                }
                
                console.error("❌ API Error Response:", errorData);
                const detail = errorData.detail;
                const msg = Array.isArray(detail)
                    ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
                    : (detail || `HTTP error! status: ${response.status}`);
                throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
            }
            
            if (response.status === 204) return null;
            const result = await response.json();
            console.log("✅ API Success Response:", result);
            return result;

        } catch (error) {
            console.error('💥 API Error:', error);
            throw error;
        }
    }

    // Public API
    getStats() {
        return this.fetchJSON(`${API_BASE_URL}/stats`);
    }

    getCampaigns() {
        return this.fetchJSON(`${API_BASE_URL}/campaigns`);
    }
    
    getCampaign(id) {
        return this.fetchJSON(`${API_BASE_URL}/campaigns/${id}`);
    }
    
    getRecentDonations(limit = 10) {
        return this.fetchJSON(`${API_BASE_URL}/donations/recent?limit=${limit}`);
    }

    getCampaignDonations(campaignId) {
        return this.fetchJSON(`${API_BASE_URL}/campaigns/${campaignId}/donations`);
    }

    createDonation(donationData) {
        return this.fetchJSON(`${API_BASE_URL}/donate`, {
            method: 'POST',
            body: JSON.stringify(donationData)
        });
    }

    // Public campaign creation (if allowed)
    createCampaign(campaignData) {
        return this.fetchJSON(`${API_BASE_URL}/campaigns`, {
            method: 'POST',
            body: JSON.stringify(campaignData)
        });
    }

    // Transparency API
    getAllTransparencyData() {
        return this.fetchJSON(`${API_BASE_URL}/transparency/all-data`);
    }

    // Volunteer Application API
    submitVolunteerApplication(applicationData) {
        return this.fetchJSON(`${API_BASE_URL}/volunteer/apply`, {
            method: 'POST',
            body: JSON.stringify(applicationData)
        });
    }

    // Admin Authentication
    adminLogin(password) {
        return this.fetchJSON(`${API_BASE_URL}/admin/login`, {
            method: 'POST',
            body: JSON.stringify({ password })
        });
    }

    // Admin Volunteer Applications Management
    getVolunteerApplications(token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/volunteer-applications`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    }

    updateVolunteerApplicationStatus(id, status, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/volunteer-applications/${id}/status`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ status })
        });
    }

    // Admin Dashboard
    getDashboardStats(token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/dashboard`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    }

    // Admin Campaign Management
    createCampaignAsAdmin(campaignData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/campaigns`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(campaignData)
        });
    }

    updateCampaign(id, campaignData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/campaigns/${id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(campaignData)
        });
    }

    deleteCampaign(id, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/campaigns/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
    }
    
    // Admin Donation Management
    createDonationByAdmin(donationData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/donations`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(donationData)
        });
    }

    // Alias for backward compatibility
    createDonationAsAdmin(donationData, token) {
        return this.createDonationByAdmin(donationData, token);
    }

    updateDonation(id, donationData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/donations/${id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(donationData)
        });
    }

    deleteDonation(id, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/donations/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
    }

    // Utility methods for data formatting
    formatCurrency(amount) {
        return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }

    formatDate(dateString) {
        return new Date(dateString).toLocaleString('ko-KR');
    }

    // Error handling helper
    handleError(error) {
        console.error('TraumaHelp API Error:', error);
        
        if (error.message.includes('401')) {
            // Handle unauthorized access
            localStorage.removeItem('adminToken');
            window.location.href = '/admin';
        }
        
        return {
            success: false,
            message: error.message || '알 수 없는 오류가 발생했습니다.',
            error: error
        };
    }

    // Batch operations for efficiency
    async getBatchData() {
        try {
            const [stats, campaigns, recentDonations] = await Promise.all([
                this.getStats(),
                this.getCampaigns(),
                this.getRecentDonations(10)
            ]);
            
            return {
                stats,
                campaigns,
                recentDonations
            };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async getAdminBatchData(token) {
        try {
            const [dashboardStats, transparencyData] = await Promise.all([
                this.getDashboardStats(token),
                this.getAllTransparencyData()
            ]);
            
            return {
                stats: dashboardStats.stats,
                campaigns: transparencyData.campaigns,
                donations: transparencyData.donations
            };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Stories (public) — 응답: { stories, total, page, limit }
    async getStories(category = null, page = 1, limit = 50) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (category && category !== "all") params.set("category", category);
        const data = await this.fetchJSON(`${API_BASE_URL}/stories?${params.toString()}`);
        return normalizeStoriesResponse(data);
    }

    getStory(storyId) {
        return this.fetchJSON(`${API_BASE_URL}/stories/${storyId}`);
    }

    // Stories (admin)
    createStory(storyData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/stories`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify(storyData),
        });
    }

    updateStory(storyId, storyData, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/stories/${storyId}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify(storyData),
        });
    }

    deleteStory(storyId, token) {
        return this.fetchJSON(`${API_BASE_URL}/admin/stories/${storyId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
    }

    /**
     * 스토리 미디어 업로드 (Supabase Storage)
     * 백엔드에 /api/admin/stories/media 없으면 /api/upload/image 로 폴백
     */
    async uploadStoryMedia(file, { category, storyId, kind = "thumbnail", pageIndex = 0 }, token) {
        const url = `${API_BASE_URL}/admin/stories/media`;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", category);
        if (storyId) formData.append("story_id", String(storyId));
        formData.append("kind", kind);
        if (kind === "page") formData.append("page_index", String(pageIndex));

        const response = await fetch(url, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        const responseText = await response.text();

        if (response.status === 405 || response.status === 404) {
            const legacy = await this.uploadImage(file, token);
            const stored = legacy.url || legacy.publicUrl || "";
            return {
                success: true,
                publicUrl: /^https?:\/\//i.test(stored) ? stored : stored,
                path: stored,
            };
        }

        if (!response.ok) {
            let errMsg = responseText;
            try {
                const j = JSON.parse(responseText);
                errMsg = j.detail || j.message || responseText;
                if (Array.isArray(errMsg)) errMsg = errMsg.map((d) => d.msg || JSON.stringify(d)).join("; ");
            } catch (e) {
                /* keep text */
            }
            throw new Error(typeof errMsg === "string" ? errMsg : `Upload failed: ${response.status}`);
        }

        const data = JSON.parse(responseText);
        return { ...data, url: data.publicUrl };
    }

    /** 레거시 로컬 업로드 (/api/upload/image) */
    async uploadImage(file, token) {
        const url = `${API_BASE_URL}/upload/image`;
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(url, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        });
        const responseText = await response.text();
        if (!response.ok) {
            let errMsg = responseText;
            try {
                const j = JSON.parse(responseText);
                errMsg = j.detail || j.message || responseText;
                if (Array.isArray(errMsg)) errMsg = errMsg.map((d) => d.msg || JSON.stringify(d)).join("; ");
            } catch (e) {
                /* keep text */
            }
            throw new Error(typeof errMsg === "string" ? errMsg : `Upload failed: ${response.status}`);
        }
        return JSON.parse(responseText);
    }
}

const api = new TraumaHelpAPI();
export default api;